import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2, Mail, ArrowLeft, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DeleteAccountPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (res.ok) setUser(await res.json());
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleDelete = async () => {
    if (confirmText.toUpperCase() !== 'ELIMINAR') {
      setError('Escribe la palabra "ELIMINAR" para confirmar.');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/delete-account`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setError('No se pudo eliminar la cuenta. Escribenos a privacidad@mblavatsky.com.');
        setDeleting(false);
        return;
      }
      setDone(true);
      setTimeout(() => (window.location.href = '/'), 3500);
    } catch {
      setError('Error de conexion.');
      setDeleting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-card p-8 max-w-md text-center" data-testid="delete-account-done">
          <div className="text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-bold text-green-400 mb-3">Cuenta eliminada</h1>
          <p className="text-[var(--text-secondary)] mb-4">
            Todos tus datos han sido borrados permanentemente. Gracias por haber sido parte de Madame Blavatsky.
          </p>
          <p className="text-[var(--text-muted)] text-xs">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-purple-400 text-sm mb-6 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </Link>

        <h1 className="text-3xl font-bold text-purple-400 mb-2">Eliminar cuenta y datos</h1>
        <p className="text-[var(--text-muted)] mb-8">
          Aplicable a la aplicacion web (app.mblavatsky.com) y a la aplicacion movil "Madame Blavatsky - Tarot".
        </p>

        <div className="glass-card p-6 mb-6 border border-red-400/20">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-bold text-red-300 mb-2">Esta accion es permanente</h2>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                Al eliminar tu cuenta se borraran DE FORMA INMEDIATA e IRREVERSIBLE todos tus datos personales asociados a Madame Blavatsky:
              </p>
            </div>
          </div>
          <ul className="text-sm text-[var(--text-secondary)] space-y-1 pl-2 list-disc list-inside">
            <li>Perfil (nombre, correo, foto, fecha de nacimiento)</li>
            <li>Historial de lecturas de tarot</li>
            <li>Cartas natales, sinastrias e I Ching</li>
            <li>Calculos de numerologia</li>
            <li>Logros e insignias</li>
            <li>Suscripciones de notificaciones push</li>
            <li>Codigos de referido y referencias</li>
            <li>Lecturas compartidas por enlace publico</li>
          </ul>
          <p className="text-xs text-[var(--text-muted)] mt-4">
            <strong>Datos que se conservan por obligacion legal:</strong> Registros de facturacion (10 anios segun normativa fiscal italiana),
            anonimizados y sin vinculacion con tu identidad.
          </p>
        </div>

        {loading ? (
          <div className="glass-card p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-400" />
          </div>
        ) : user ? (
          <div className="glass-card p-6" data-testid="delete-account-logged-in">
            <h3 className="text-lg font-semibold text-purple-300 mb-3">Estas identificado como:</h3>
            <div className="p-3 rounded bg-white/5 mb-6">
              <p className="text-sm text-[var(--text-secondary)]">{user.name}</p>
              <p className="text-xs text-[var(--text-muted)]">{user.email}</p>
            </div>

            <label className="block text-sm text-[var(--text-secondary)] mb-2">
              Para confirmar, escribe la palabra <strong className="text-red-400">ELIMINAR</strong> en el recuadro:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ELIMINAR"
              className="mystic-input w-full mb-4"
              data-testid="delete-account-confirm-input"
              autoComplete="off"
            />

            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

            <button
              onClick={handleDelete}
              disabled={deleting}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50"
              data-testid="delete-account-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {deleting ? 'Eliminando...' : 'Eliminar mi cuenta permanentemente'}
            </button>

            <p className="text-xs text-[var(--text-muted)] mt-3 text-center">
              La eliminacion es instantanea. No podras recuperar tu cuenta ni tus datos.
            </p>
          </div>
        ) : (
          <div className="glass-card p-6" data-testid="delete-account-not-logged-in">
            <h3 className="text-lg font-semibold text-purple-300 mb-3">Metodo 1 — Elimina tu cuenta desde la app</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Inicia sesion en la app o el sitio web con tu cuenta de Google, y vuelve a esta pagina.
              Aparecera un boton para eliminar tu cuenta directamente.
            </p>
            <button
              onClick={() => navigate('/')}
              className="btn-gold w-full mb-6"
            >
              Iniciar sesion
            </button>

            <h3 className="text-lg font-semibold text-purple-300 mb-3">Metodo 2 — Solicitud por correo</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Si no puedes iniciar sesion o prefieres solicitar la eliminacion por escrito, envianos un correo a:
            </p>
            <a
              href="mailto:privacidad@mblavatsky.com?subject=Solicitud%20de%20eliminaci%C3%B3n%20de%20cuenta&body=Hola%2C%0A%0ASolicito%20la%20eliminaci%C3%B3n%20de%20mi%20cuenta%20y%20datos%20en%20Madame%20Blavatsky.%0A%0ACorreo%20de%20mi%20cuenta%3A%20%5Btu%20correo%5D%0A%0AGracias."
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-400/30 transition-colors"
            >
              <Mail className="w-4 h-4" /> privacidad@mblavatsky.com
            </a>
            <p className="text-xs text-[var(--text-muted)] mt-3">
              Responderemos y procesaremos tu solicitud en un plazo maximo de 30 dias, tal como establece el RGPD.
            </p>
          </div>
        )}

        <div className="glass-card p-6 mt-6">
          <h3 className="text-lg font-semibold text-purple-300 mb-3">Plazos y datos que se conservan</h3>
          <div className="space-y-3 text-sm text-[var(--text-secondary)]">
            <div>
              <p><strong>Perfil y contenido de lecturas:</strong> Eliminados inmediatamente al confirmar.</p>
            </div>
            <div>
              <p><strong>Copias de seguridad tecnicas:</strong> Purgadas en un plazo maximo de 30 dias.</p>
            </div>
            <div>
              <p><strong>Datos de facturacion:</strong> Conservados 10 anios (obligacion fiscal italiana - D.P.R. 600/1973),
              disociados de tu identidad personal.</p>
            </div>
            <div>
              <p><strong>Logs tecnicos anonimizados:</strong> Retenidos hasta 90 dias, sin datos identificativos.</p>
            </div>
          </div>
        </div>

        <p className="text-xs text-[var(--text-muted)] text-center mt-6">
          Consulta nuestra <Link to="/privacy" className="text-purple-400 underline">Politica de Privacidad</Link> para mas detalles.
        </p>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
